import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import {
  getDefaultFloatingModalLayout,
  getLayoutViewportSize,
  PAGE_ZOOM_CHANGED_EVENT,
} from '../utils/pageZoom';

interface TimerModalProps {
  isOpen: boolean;
  onClose: () => void;
  isHidden?: boolean;
  onRequestHide?: () => void;
  onRunningChange?: (running: boolean) => void;
}

const TimerModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  pointer-events: auto;
`;

const TimerModalContent = styled.div<{ $isMinimized: boolean; $isMaximized: boolean; $left: number; $top: number; $width: number; $height: number }>`
  position: fixed;
  left: ${props => props.$left}px;
  top: ${props => props.$top}px;
  width: ${props => props.$isMinimized ? '200px' : `${props.$width}px`};
  height: ${props => props.$isMinimized ? '80px' : `${props.$height}px`};
  background: var(--color-accent-strong);
  border: ${props => props.$isMaximized ? 'none' : '2px solid rgba(255, 255, 255, 0.45)'};
  border-radius: ${props => props.$isMaximized ? '0' : '12px'};
  box-shadow: ${props => props.$isMaximized ? 'none' : '0 8px 24px rgba(0, 0, 0, 0.3)'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
  transition: ${props => props.$isMinimized ? 'width 0.3s ease, height 0.3s ease' : props.$isMaximized ? 'width 0.3s ease, height 0.3s ease, border-radius 0.3s ease' : 'none'};
  min-width: ${props => props.$isMinimized ? '200px' : '300px'};
  min-height: ${props => props.$isMinimized ? '80px' : '350px'};
`;

const TimerModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  cursor: move;
  user-select: none;
  flex-shrink: 0;
  min-height: 36px;
  height: 36px;
  box-sizing: border-box;
  
  /* 다크모드: 네비바 색상 */
  :root:not([data-theme='light']) & {
    background: #404040;
  }
  
  @media (max-width: 768px) {
    min-height: 36px;
    height: 36px;
    padding: 4px 8px;
  }
`;

const TimerModalTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const TimerModalControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TimerModalButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 16px;
  line-height: 1;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const TimerModalBody = styled.div<{ $isMinimized: boolean }>`
  flex: 1;
  padding: ${props => props.$isMinimized ? '8px' : '20px'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${props => props.$isMinimized ? 'center' : 'space-around'};
  overflow: hidden;
  
  /* 다크모드: 페이지 본문 배경색 */
  :root:not([data-theme='light']) & {
    background: #141414;
  }
`;


const TimerDisplaySection = styled.div<{ $modalWidth: number; $modalHeight: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => Math.min(6, props.$modalHeight * 0.015)}px;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding: ${props => props.$modalHeight * 0.12}px ${props => Math.min(20, props.$modalHeight * 0.04)}px ${props => props.$modalHeight * 0.04}px;
  box-sizing: border-box;
`;

const TimerCircleContainer = styled.div<{ $size: number }>`
  position: relative;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  min-width: ${props => props.$size}px;
  min-height: ${props => props.$size}px;
  max-width: ${props => props.$size}px;
  max-height: ${props => props.$size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TimerTextContainer = styled.div<{ $size: number }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  z-index: 1;
  min-width: ${props => props.$size * 0.5}px;
`;

const TimerTextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-shrink: 0;
`;

const TimerButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  flex-shrink: 0;
`;

const TimerTextPart = styled.div<{ $isEditing?: boolean; $fontSize: number; $width: number }>`
  font-size: ${props => props.$fontSize * 1.1}rem;
  font-weight: 700;
  color: white;
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  line-height: 1.2;
  cursor: pointer;
  user-select: none;
  padding: ${props => props.$fontSize * 0.12}rem 0;
  margin: 0;
  border-radius: 8px;
  transition: background 0.2s ease;
  width: ${props => props.$width * 0.9}px;
  min-width: ${props => props.$width * 0.9}px;
  max-width: ${props => props.$width * 0.9}px;
  text-align: center;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const TimerTextSeparator = styled.span<{ $fontSize: number }>`
  font-size: ${props => props.$fontSize * 1.1}rem;
  font-weight: 700;
  color: white;
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  line-height: 1.2;
  user-select: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: auto;
  min-width: auto;
`;

const TimerTextInput = styled.input<{ $fontSize: number; $width: number }>`
  font-size: ${props => props.$fontSize * 1.1}rem;
  font-weight: 700;
  color: white;
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  line-height: 1.2;
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: ${props => props.$fontSize * 0.12}rem 0;
  margin: 0;
  text-align: center;
  width: ${props => props.$width * 0.9}px;
  min-width: ${props => props.$width * 0.9}px;
  max-width: ${props => props.$width * 0.9}px;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &:active {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
`;

const TimerButton = styled.button<{ $variant?: 'primary' | 'secondary'; $padding: number; $fontSize: number }>`
  padding: ${props => props.$padding}px ${props => props.$padding * 2}px;
  border: none;
  border-radius: 8px;
  font-size: ${props => props.$fontSize}px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$variant === 'primary' ? `
    background: var(--color-surface);
    color: var(--color-accent-text);
    
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.9);
    }
    
    /* 다크모드: 배경보다 조금 밝게 */
    :root:not([data-theme='light']) & {
      background: #4a4a4a;
      color: white;
      
      &:hover:not(:disabled) {
        background: #555555;
      }
    }
  ` : `
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.45);
    
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.3);
      border-color: rgba(255, 255, 255, 0.5);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const MinimizedTimerDisplay = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: white;
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0 8px;
  box-sizing: border-box;
  position: relative;
`;

const MinimizedTimerText = styled.span`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  flex: 0 0 auto;
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.1em;
`;

const MinimizedTimerButtonWrapper = styled.div`
  margin-left: auto;
  flex-shrink: 0;
`;

const MinimizedPlayStopButton = styled.button<{ $isRunning: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: ${props => props.$isRunning ? 'none' : '1px solid rgba(255, 255, 255, 0.5)'};
  background: ${props => props.$isRunning ? '#ff6b6b' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.1s ease;
  flex-shrink: 0;
  
  &:hover {
    background: ${props => props.$isRunning ? '#ff5252' : 'rgba(255, 255, 255, 0.3)'};
    border-color: ${props => props.$isRunning ? 'none' : 'rgba(255, 255, 255, 0.7)'};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
`;

const MinimizedPlayIcon = styled.svg`
  width: 9px;
  height: 9px;
  fill: currentColor;
`;

const MinimizedStopIcon = styled.svg`
  width: 8px;
  height: 8px;
  fill: currentColor;
`;

const MinimizedProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`;

const MinimizedProgressBar = styled.div<{ $progress: number; $isUnderOneMinute: boolean }>`
  height: 100%;
  width: ${props => props.$progress * 100}%;
  background: ${props => props.$isUnderOneMinute ? 'rgba(255, 107, 107, 0.8)' : 'var(--color-surface)'};
  transition: width 1s linear, background 0.3s ease;
  position: absolute;
  right: 0;
  top: 0;
`;

const QuickTimeButtonContainer = styled.div<{ $top: number }>`
  position: absolute;
  top: ${props => props.$top}px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: ${props => Math.min(8, Math.abs(props.$top) * 0.1)}px;
  z-index: 2;
  align-items: center;
  flex-wrap: nowrap;
  justify-content: center;
  white-space: nowrap;
`;

const QuickTimeButton = styled.button<{ $fontSize: number; $padding: number }>`
  padding: ${props => props.$padding}px ${props => props.$padding * 2}px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 6px;
  color: white;
  font-size: ${props => props.$fontSize}px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VolumeControlContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  margin-top: 16px;
  padding: 0 20px;
  box-sizing: border-box;
  position: relative;
`;

const VolumeSpacer = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const VolumeIconButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s ease;
  flex-shrink: 0;
  padding: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const VolumeSliderContainer = styled.div`
  flex: 1;
  position: relative;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  cursor: pointer;
  max-width: 200px;
`;

const VolumeSliderTrack = styled.div<{ $volume: number }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${props => props.$volume * 100}%;
  background: white;
  border-radius: 3px;
  transition: width 0.1s ease;
`;

const VolumeSliderThumb = styled.div<{ $volume: number }>`
  position: absolute;
  left: ${props => props.$volume * 100}%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  cursor: grab;
  transition: left 0.1s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:active {
    cursor: grabbing;
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const MaximizeIcon = styled.svg`
  width: 11px;
  height: 11px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  display: block;
`;

const RestoreIcon = styled.svg`
  width: 11px;
  height: 11px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  display: block;
`;

// 타이머 음원 캐시 (모듈 레벨에서 관리)
const timerAudioCache: { [key: string]: HTMLAudioElement | null } = {};

// 타이머 음원 미리 로드 및 캐싱
const preloadTimerSound = async (soundPath: string): Promise<HTMLAudioElement | null> => {
  // 이미 캐시에 있으면 반환
  if (timerAudioCache[soundPath]) {
    return timerAudioCache[soundPath];
  }

  try {
    const audio = new Audio(soundPath);
    
    // 오디오 로드 완료를 기다림
    await new Promise((resolve, reject) => {
      audio.addEventListener('canplaythrough', resolve, { once: true });
      audio.addEventListener('error', reject, { once: true });
      audio.load();
    });
    
    // 캐시에 저장
    timerAudioCache[soundPath] = audio;
    console.log(`타이머 음원 캐시 완료: ${soundPath}`);
    return audio;
  } catch (error) {
    console.warn(`타이머 음원 로드 실패: ${soundPath}`, error);
    timerAudioCache[soundPath] = null;
    return null;
  }
};

// 캐시된 음원을 사용하여 새 Audio 인스턴스 생성 (재생용)
const playTimerSound = (soundPath: string): HTMLAudioElement | null => {
  const cachedAudio = timerAudioCache[soundPath];
  
  if (!cachedAudio) {
    // 캐시에 없으면 새로 생성 (fallback)
    const audio = new Audio(soundPath);
    audio.onerror = () => {
      console.warn(`타이머 음원 파일을 찾을 수 없음: ${soundPath}`);
    };
    return audio;
  }

  // 캐시된 오디오의 src를 사용하여 새 인스턴스 생성 (동시 재생 가능)
  const audio = new Audio(cachedAudio.src);
  audio.onerror = cachedAudio.onerror;
  
  return audio;
};

const TimerModal: React.FC<TimerModalProps> = ({ isOpen, onClose, isHidden = false, onRequestHide, onRunningChange }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0); // 초기값을 0으로 설정
  const [isStopwatch, setIsStopwatch] = useState(false); // 스탑워치 모드
  const [editingPart, setEditingPart] = useState<'minutes' | 'seconds' | null>(null);
  const [editingMinutes, setEditingMinutes] = useState('00');
  const [editingSeconds, setEditingSeconds] = useState('00');
  const [volume, setVolume] = useState<number>(1.0); // 0.0 ~ 1.0
  const [isMuted, setIsMuted] = useState<boolean>(false);
  
  // 모달 위치 및 크기
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [size, setSize] = useState({ width: 450, height: 550 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });
  const [resizeDirection, setResizeDirection] = useState<'nw' | 'ne' | 'sw' | 'se' | null>(null);
  
  // 전체화면 모드일 때 창 크기 (반응형: resize 시 갱신)
  const [windowSize, setWindowSize] = useState(() => {
    if (typeof window === 'undefined') return { w: 1920, h: 1080 };
    const { width, height } = getLayoutViewportSize();
    return { w: width, h: height };
  });

  // 모달이 열릴 때마다 화면 80% 크기 + 정중앙 배치로 자동 세팅 (CSS zoom 보정)
  useEffect(() => {
    if (!isOpen) return;
    if (typeof window === 'undefined') return;

    setIsMinimized(false);
    setIsMaximized(false);

    const { size: nextSize, position: nextPosition, viewport } = getDefaultFloatingModalLayout();
    setSize(nextSize);
    setPosition(nextPosition);
    setWindowSize({ w: viewport.w, h: viewport.h });

    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  }, [isOpen]);
  
  useEffect(() => {
    if (!isOpen || !isMaximized) return;
    const syncViewport = () => {
      const { width, height } = getLayoutViewportSize();
      setWindowSize({ w: width, h: height });
    };
    syncViewport();
    window.addEventListener('resize', syncViewport);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, syncViewport);
    return () => {
      window.removeEventListener('resize', syncViewport);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, syncViewport);
    };
  }, [isOpen, isMaximized]);

  useEffect(() => {
    if (!isOpen || isMaximized || isMinimized) return;
    const onPageZoomChange = () => {
      const { size: nextSize, position: nextPosition, viewport } = getDefaultFloatingModalLayout();
      setSize(nextSize);
      setPosition(nextPosition);
      setWindowSize({ w: viewport.w, h: viewport.h });
    };
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, onPageZoomChange);
    return () => window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, onPageZoomChange);
  }, [isOpen, isMaximized, isMinimized]);
  
  const modalWidth = isMaximized ? windowSize.w : size.width;
  const modalHeight = isMaximized ? windowSize.h : size.height;
  const modalLeft = isMaximized ? 0 : position.x;
  const modalTop = isMaximized ? 0 : position.y;
  
  const modalRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasPlayedOneMinuteSound = useRef<boolean>(false);
  const audioStopTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 모달이 열릴 때 음원 미리 로드
  useEffect(() => {
    if (isOpen) {
      preloadTimerSound('/sounds/EDUCOORDI_TIMER_1MIN.mp3').catch(console.error);
    }
  }, [isOpen]);

  // 모달이 닫힐 때 타이머 및 음원 정리
  useEffect(() => {
    if (!isOpen) {
      setIsRunning(false);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      // 음원 정지 타이머 취소
      if (audioStopTimeoutRef.current) {
        clearTimeout(audioStopTimeoutRef.current);
        audioStopTimeoutRef.current = null;
      }
      // 음원 정지
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      hasPlayedOneMinuteSound.current = false;
      setIsStopwatch(false);
    }
  }, [isOpen]);

  useEffect(() => {
    onRunningChange?.(isRunning);
  }, [isRunning, onRunningChange]);

  // 타이머 카운트다운 / 스탑워치 카운트업
  useEffect(() => {
    if (!isOpen) return;
    
    if (isRunning) {
      timerIntervalRef.current = setInterval(() => {
        if (isStopwatch) {
          // 스탑워치 모드: 카운트업 (음원 재생 없음)
        setRemainingSeconds((prev) => {
            const newValue = prev + 1;
            return newValue;
          });
        } else {
          // 타이머 모드: 카운트다운
          if (remainingSeconds > 0) {
            setRemainingSeconds((prev) => {
              const newValue = prev - 1;
              
              // 1분(60초) 이하일 때 음원 재생 (한 번만)
              if (newValue <= 60 && !hasPlayedOneMinuteSound.current) {
                hasPlayedOneMinuteSound.current = true;
                
                // 기존 음원이 재생 중이면 정지
                if (audioRef.current) {
                  audioRef.current.pause();
                  audioRef.current.currentTime = 0;
                }
                
                // 캐시된 음원 사용하여 재생
                const audio = playTimerSound('/sounds/EDUCOORDI_TIMER_1MIN.mp3');
                if (audio) {
                  audioRef.current = audio;
                  audio.volume = isMuted ? 0 : volume;
                  audio.play().catch((error) => {
                    console.error('타이머 1분 알람 음원 재생 실패:', error);
                  });
                }
              } else if (audioRef.current && !audioRef.current.paused) {
                // 재생 중인 음원의 음량 업데이트
                audioRef.current.volume = isMuted ? 0 : volume;
              }
              
              if (newValue <= 0) {
            setIsRunning(false);
                // 음원을 2초 후에 정지 (여운 있게 끝나도록)
                if (audioRef.current) {
                  // 기존 타이머가 있으면 취소
                  if (audioStopTimeoutRef.current) {
                    clearTimeout(audioStopTimeoutRef.current);
                  }
                  // 2초 후에 음원 정지
                  audioStopTimeoutRef.current = setTimeout(() => {
                    if (audioRef.current) {
                      audioRef.current.pause();
                      audioRef.current.currentTime = 0;
                      audioRef.current = null;
                    }
                    audioStopTimeoutRef.current = null;
                  }, 2000);
                }
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
                // 플래그 초기화 (다음 타이머를 위해)
                hasPlayedOneMinuteSound.current = false;
            return 0;
          }
              return newValue;
        });
          }
        }
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isOpen, isRunning, remainingSeconds, isStopwatch, isMuted, volume]);
  
  // 음량 변경 시 재생 중인 음원의 음량 업데이트
  useEffect(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // 드래그 시작 (마우스)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isMaximized) return;
    // 버튼, 입력 필드, 슬라이더 등 인터랙티브 요소는 제외
    const target = e.target as HTMLElement;
    if (target.closest('button') || 
        target.closest('input') || 
        target.closest('[data-volume-slider]') ||
        target.closest('[data-volume-handle]') ||
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT') return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  }, [position, isMaximized]);

  // 드래그 시작 (터치)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isMaximized) return;
    // 버튼, 입력 필드, 슬라이더 등 인터랙티브 요소는 제외
    const target = e.target as HTMLElement;
    if (target.closest('button') || 
        target.closest('input') || 
        target.closest('[data-volume-slider]') ||
        target.closest('[data-volume-handle]') ||
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT') return;
    
    const touch = e.touches[0];
    if (!touch) return;
    
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    });
    e.preventDefault(); // 스크롤 방지
  }, [position, isMaximized]);

  // 드래그 중
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
      e.preventDefault(); // 스크롤 방지
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragStart]);

  // 리사이즈 핸들
  const handleResizeMouseDown = useCallback((e: React.MouseEvent, direction: 'nw' | 'ne' | 'sw' | 'se') => {
    if (isMinimized || isMaximized) return;
    e.stopPropagation();
    setResizeDirection(direction);
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      left: position.x,
      top: position.y,
    });
  }, [size, position, isMinimized, isMaximized]);

  // 리사이즈 중
  useEffect(() => {
    if (!isResizing || !resizeDirection) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;
      let newX = resizeStart.left;
      let newY = resizeStart.top;
      
      switch (resizeDirection) {
        case 'nw': // 왼쪽 상단
          newWidth = Math.max(300, resizeStart.width - deltaX);
          newHeight = Math.max(350, resizeStart.height - deltaY);
          newX = resizeStart.left + (resizeStart.width - newWidth);
          newY = resizeStart.top + (resizeStart.height - newHeight);
          break;
        case 'ne': // 오른쪽 상단
          newWidth = Math.max(300, resizeStart.width + deltaX);
          newHeight = Math.max(350, resizeStart.height - deltaY);
          newY = resizeStart.top + (resizeStart.height - newHeight);
          break;
        case 'sw': // 왼쪽 하단
          newWidth = Math.max(300, resizeStart.width - deltaX);
          newHeight = Math.max(350, resizeStart.height + deltaY);
          newX = resizeStart.left + (resizeStart.width - newWidth);
          break;
        case 'se': // 오른쪽 하단
          newWidth = Math.max(300, resizeStart.width + deltaX);
          newHeight = Math.max(350, resizeStart.height + deltaY);
          break;
      }
      
      setSize({
        width: newWidth,
        height: newHeight,
      });
      
      setPosition({
        x: newX,
        y: newY,
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeDirection, resizeStart]);

  // 시간 포맷팅
  const formatTime = useCallback((totalSecs: number) => {
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // 분 클릭 핸들러
  const handleMinutesClick = useCallback(() => {
    if (isRunning) return;
    setEditingPart('minutes');
    setEditingMinutes(''); // 기존 값 지우고 새로 입력
  }, [isRunning]);

  // 초 클릭 핸들러
  const handleSecondsClick = useCallback(() => {
    if (isRunning) return;
    setEditingPart('seconds');
    setEditingSeconds(''); // 기존 값 지우고 새로 입력
  }, [isRunning]);

  // 분 입력 완료 핸들러
  const handleMinutesBlur = useCallback(() => {
    setEditingPart(null);
    const mins = Math.max(0, Math.min(59, parseInt(editingMinutes) || 0));
    setMinutes(mins);
    const total = mins * 60 + seconds;
    setTotalSeconds(total);
    if (!isRunning) {
      setRemainingSeconds(total);
    }
    setEditingMinutes(''); // 입력 필드 초기화
  }, [editingMinutes, seconds, isRunning]);

  // 초 입력 완료 핸들러
  const handleSecondsBlur = useCallback(() => {
    setEditingPart(null);
    const secs = Math.max(0, Math.min(59, parseInt(editingSeconds) || 0));
    setSeconds(secs);
    const total = minutes * 60 + secs;
    setTotalSeconds(total);
    if (!isRunning) {
      setRemainingSeconds(total);
    }
    setEditingSeconds(''); // 입력 필드 초기화
  }, [editingSeconds, minutes, isRunning]);

  // 분 입력 키 핸들러
  const handleMinutesKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleMinutesBlur();
    } else if (e.key === 'Escape') {
      setEditingPart(null);
    }
  }, [handleMinutesBlur]);

  // 초 입력 키 핸들러
  const handleSecondsKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSecondsBlur();
    } else if (e.key === 'Escape') {
      setEditingPart(null);
    }
  }, [handleSecondsBlur]);

  // 진행률 계산
  const progress = useMemo(() => {
    if (isStopwatch) {
      // 스탑워치 모드: 현재 초를 60으로 나눈 나머지로 1분 내 진행률 계산
      return (remainingSeconds % 60) / 60;
    }
    if (totalSeconds === 0) return 0;
    return remainingSeconds / totalSeconds;
  }, [remainingSeconds, totalSeconds, isStopwatch]);
  
  // 스탑워치 모드에서 완료된 1분 수
  const completedMinutes = useMemo(() => {
    if (!isStopwatch) return 0;
    return Math.floor(remainingSeconds / 60);
  }, [remainingSeconds, isStopwatch]);
  
  // 스탑워치 모드에서 현재 1분이 짝수 번째인지 (0, 2, 4, ... = 흰색, 1, 3, 5, ... = 다른 색상)
  const isCurrentMinuteEven = useMemo(() => {
    if (!isStopwatch) return true;
    return Math.floor(remainingSeconds / 60) % 2 === 0;
  }, [remainingSeconds, isStopwatch]);
  
  // 다크모드 상태 관리
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') !== 'light';
    }
    return false;
  });
  
  // 다크모드 변경 감지
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
          setIsDarkMode(isDark);
        }
      });
    });
    
    if (typeof document !== 'undefined') {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme']
      });
    }
    
    return () => {
      observer.disconnect();
    };
  }, []);
  
  // 스탑워치 모드 색상 결정
  const getStopwatchColor = useCallback((isEven: boolean) => {
    if (isEven) return 'white';
    return isDarkMode ? '#5B5B5B' : '#5F8581';
  }, [isDarkMode]);

  // 1분 이하 여부
  const isUnderOneMinute = useMemo(() => {
    return remainingSeconds <= 60 && remainingSeconds > 0;
  }, [remainingSeconds]);

  // 타이머 시작
  const handleStart = useCallback(() => {
    if (remainingSeconds > 0) {
      // 정지 상태에서 시작: 현재 설정된 시간을 처음으로 리셋하여 시작
      setTotalSeconds(remainingSeconds);
      setIsStopwatch(false);
      setIsRunning(true);
      setEditingPart(null);
      hasPlayedOneMinuteSound.current = false;
      
      // 1분 이하로 시작하는 경우 즉시 음원 재생
      if (remainingSeconds <= 60) {
        hasPlayedOneMinuteSound.current = true;
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        const audio = playTimerSound('/sounds/EDUCOORDI_TIMER_1MIN.mp3');
        if (audio) {
          audioRef.current = audio;
          audio.volume = isMuted ? 0 : volume;
          audio.play().catch((error) => {
            console.error('타이머 1분 알람 음원 재생 실패:', error);
          });
        }
      }
    } else {
      // 처음 시작
      // editingMinutes와 editingSeconds를 우선적으로 사용 (사용자가 입력한 값)
      const editingMin = parseInt(editingMinutes) || 0;
      const editingSec = parseInt(editingSeconds) || 0;
      const editingTotal = editingMin * 60 + editingSec;
      
      // 0초일 때는 항상 스탑워치 모드로 시작
      if (editingTotal <= 0 || (editingMin === 0 && editingSec === 0)) {
        setIsStopwatch(true);
        setRemainingSeconds(0);
        setTotalSeconds(0);
      setIsRunning(true);
      setEditingPart(null);
        hasPlayedOneMinuteSound.current = false;
        return;
      }
      
      // 타이머 모드 - editingMinutes와 editingSeconds를 사용
      setIsStopwatch(false);
      setTotalSeconds(editingTotal);
      setRemainingSeconds(editingTotal);
      // minutes와 seconds도 업데이트
      setMinutes(editingMin);
      setSeconds(editingSec);
      setIsRunning(true);
      setEditingPart(null);
      
      // 음원 재생 플래그 초기화 (항상 초기화)
      hasPlayedOneMinuteSound.current = false;
      
      // 1분 이하로 시작하는 경우 즉시 음원 재생
      if (editingTotal <= 60 && editingTotal > 0) {
        hasPlayedOneMinuteSound.current = true;
        
        // 기존 음원이 재생 중이면 정지
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        
          // 캐시된 음원 사용하여 재생
          const audio = playTimerSound('/sounds/EDUCOORDI_TIMER_1MIN.mp3');
          if (audio) {
            audioRef.current = audio;
            audio.volume = isMuted ? 0 : volume;
            audio.play().catch((error) => {
              console.error('타이머 1분 알람 음원 재생 실패:', error);
            });
          }
      }
    }
  }, [remainingSeconds, isMuted, volume, editingMinutes, editingSeconds]);

  // 타이머 일시정지
  const handlePause = useCallback(() => {
    setIsRunning(false);
    // 일시정지 시 음원도 일시정지 (정지하지 않고 일시정지만)
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }
    // 기존 정지 타이머가 있으면 취소 (일시정지이므로 2초 후 정지하지 않음)
    if (audioStopTimeoutRef.current) {
      clearTimeout(audioStopTimeoutRef.current);
      audioStopTimeoutRef.current = null;
    }
  }, []);

  // 타이머 리셋 (00:00으로 초기화)
  const handleReset = useCallback(() => {
    setIsRunning(false);
    setIsStopwatch(false);
    setRemainingSeconds(0);
    setTotalSeconds(0);
    setMinutes(0);
    setSeconds(0);
    setEditingPart(null);
    setEditingMinutes('00');
    setEditingSeconds('00');
    
    // 음원 정지 타이머 취소
    if (audioStopTimeoutRef.current) {
      clearTimeout(audioStopTimeoutRef.current);
      audioStopTimeoutRef.current = null;
    }
    
    // 음원 정지 및 플래그 초기화
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    hasPlayedOneMinuteSound.current = false;
  }, []);

  // 음량 조절 핸들러
  const handleVolumeChange = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
    setIsMuted(false);
    
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
  }, []);

  // 음소거 토글 핸들러
  const handleMuteToggle = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    
    if (audioRef.current) {
      audioRef.current.volume = newMuted ? 0 : volume;
    }
  }, [isMuted, volume]);

  // 음량 슬라이더 클릭 핸들러
  const handleVolumeSliderClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newVolume = Math.max(0, Math.min(1, x / rect.width));
    handleVolumeChange(newVolume);
  }, [handleVolumeChange]);

  // 음량 슬라이더 드래그 핸들러
  const [isDraggingVolume, setIsDraggingVolume] = useState(false);
  
  const handleVolumeSliderMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setIsDraggingVolume(true);
    handleVolumeSliderClick(e);
  }, [handleVolumeSliderClick]);

  useEffect(() => {
    if (!isDraggingVolume) return;

    const handleMouseMove = (e: MouseEvent) => {
      const slider = document.querySelector('[data-volume-slider]') as HTMLElement;
      if (!slider) return;
      
      const rect = slider.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const newVolume = Math.max(0, Math.min(1, x / rect.width));
      handleVolumeChange(newVolume);
    };

    const handleMouseUp = () => {
      setIsDraggingVolume(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingVolume, handleVolumeChange]);

  // 빠른 시간 추가 (반복 클릭으로 시간 누적, 정지 상태에서만 가능. 시작은 시작 버튼으로.)
  const handleAddTime = useCallback((secondsToAdd: number) => {
    if (isRunning) return; // 실행 중일 때는 추가 불가
    
    const newRemainingSeconds = remainingSeconds + secondsToAdd;
    const newTotalSeconds = newRemainingSeconds;
    
    setRemainingSeconds(newRemainingSeconds);
    setTotalSeconds(newTotalSeconds);
    
    const newMinutes = Math.floor(newRemainingSeconds / 60);
    const newSeconds = newRemainingSeconds % 60;
    setMinutes(newMinutes);
    setSeconds(newSeconds);
    setEditingMinutes(String(newMinutes).padStart(2, '0'));
    setEditingSeconds(String(newSeconds).padStart(2, '0'));
    setEditingPart(null);
    // 타이머(카운트다운) 모드로 표시
    setIsStopwatch(false);
  }, [isRunning, remainingSeconds]);

  // 모달 크기에 따른 동적 크기 계산
  const circleSize = useMemo(() => {
    if (isMinimized) return 0;
    // 모달 크기에 비례하여 계산, 최소 220px만 유지하고 최대값 제한 제거
    const availableWidth = modalWidth - 40; // 패딩 고려
    const availableHeight = modalHeight - 120; // 헤더 + 버튼 영역 고려
    // 세로 공간을 더 활용하도록 높이를 우선 고려
    const size = Math.min(availableWidth, availableHeight * 0.75);
    return Math.max(220, size);
  }, [modalWidth, modalHeight, isMinimized]);

  const radius = useMemo(() => {
    if (circleSize === 0) return 0;
    // strokeWidth를 고려하여 정확한 반지름 계산
    const strokeWidth = circleSize / 17.5;
    return (circleSize / 2) - (strokeWidth / 2) - 2; // 2px 여유 공간
  }, [circleSize]);
  const circumference = useMemo(() => 2 * Math.PI * radius, [radius]);
  
  // 텍스트 크기 계산 (원형 크기에 비례)
  const textFontSize = useMemo(() => {
    if (circleSize === 0) return 4;
    // 원형 크기의 약 17.1%를 폰트 크기로 사용 (350px 기준 6rem = 약 60px)
    const fontSize = circleSize * 0.171;
    // rem 단위로 변환 (기본 폰트 크기 16px 기준)
    const fontSizeRem = fontSize / 16;
    return Math.max(1.5, Math.min(fontSizeRem, 7));
  }, [circleSize]);

  const textWidth = useMemo(() => {
    if (circleSize === 0) return 80;
    // 원형 크기의 약 22.9%를 텍스트 너비로 사용 (350px 기준 80px)
    return circleSize * 0.229;
  }, [circleSize]);

  // 버튼 크기 계산 (원형 크기에 비례, 덜 커지도록 조정)
  const buttonFontSize = useMemo(() => {
    if (circleSize === 0) return 12;
    // 원형 크기의 약 3.5%를 폰트 크기로 사용 (350px 기준 약 12px)
    const fontSize = circleSize * 0.035;
    return Math.max(10, Math.min(fontSize, 18));
  }, [circleSize]);

  const buttonPadding = useMemo(() => {
    if (circleSize === 0) return 8;
    // 원형 크기의 약 2.5%를 패딩으로 사용 (350px 기준 약 9px)
    const padding = circleSize * 0.025;
    return Math.max(6, Math.min(padding, 14));
  }, [circleSize]);

  // SVG 원의 둘레
  // 12시 방향에서 시계방향으로 생성/줄어들도록
  // SVG는 rotate(-90deg)로 12시가 시작점이 되고, 기본적으로 반시계방향으로 그려짐
  // 원을 scaleY(-1)로 뒤집으면 시계방향이 되고, 시작점은 12시로 유지됨
  // 타이머 모드: progress가 1(전체 시간)일 때 0 (전체 표시), 0(시간 끝)일 때 circumference (모두 가려짐)
  // 스탑워치 모드: progress가 0(1분 시작)일 때 circumference (시작, 모두 가려짐), 1(1분 완료)일 때 0 (완료, 전체 표시)
  // 뒤집으면 계산이 반대로 되어야 하므로 둘 다 (1 - progress)를 사용
  const strokeDashoffset = circumference * (1 - progress);
  
  // QuickTimeButton 위치 계산 (원형과 최소 간격 유지)
  const quickButtonTop = useMemo(() => {
    if (circleSize === 0) return -30;
    // 원래 방식: 원형 크기의 8.5%를 기본 위치로 사용
    const baseTop = -Math.max(30, circleSize * 0.085);
    // 최소 간격(20px) 추가
    const minGap = 20;
    return baseTop - minGap;
  }, [circleSize]);

  if (!isOpen || isHidden) return null;

  return createPortal(
    <TimerModalOverlay
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          // 요구사항:
          // - 최소화 상태에서는 배경 클릭해도 닫히지 않음(아무 동작도 하지 않음)
          // - 중간(일반) 상태에서는 배경 클릭 시 화면에서만 숨기고(호버 버튼 활성),
          //   타이머는 계속 동작
          if (isMinimized) return;
          if (onRequestHide) onRequestHide();
          else onClose();
        }
      }}
      onTouchStart={(e) => {
        if (e.target === e.currentTarget) {
          if (isMinimized) return;
          if (onRequestHide) onRequestHide();
          else onClose();
        }
      }}
    >
      <TimerModalContent
        ref={modalRef}
        $isMinimized={isMinimized}
        $isMaximized={isMaximized}
        $left={modalLeft}
        $top={modalTop}
        $width={modalWidth}
        $height={modalHeight}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <TimerModalHeader
          ref={headerRef}
        >
          <TimerModalTitle>타이머</TimerModalTitle>
          <TimerModalControls>
            <TimerModalButton
              onClick={() => {
                // 항상 미니 모드로 변환
                setIsMinimized(true);
                setIsMaximized(false);
              }}
              title="최소화"
              disabled={isMinimized}
            >
              −
            </TimerModalButton>
            <TimerModalButton
              onClick={() => {
                if (isMinimized) {
                  // 미니 모드 → 일반 모드
                  setIsMinimized(false);
                } else if (isMaximized) {
                  // 전체 화면 모드 → 일반 모드
                  setIsMaximized(false);
                } else {
                  // 일반 모드 → 전체 화면 모드
                  setIsMaximized(true);
                }
              }}
              title={isMinimized ? "확대" : isMaximized ? "일반 크기" : "전체 화면"}
            >
              {(isMinimized || isMaximized) ? (
                <RestoreIcon viewBox="0 0 16 16">
                  {/* 앞의 사각형 (왼쪽 아래) */}
                  <rect x="2" y="4" width="10" height="10" rx="1" strokeWidth="1.5" fill="rgba(255, 255, 255, 0.2)" />
                  {/* 뒤의 사각형 (오른쪽 상단으로 튀어나옴) */}
                  <rect x="4" y="2" width="10" height="10" rx="1" strokeWidth="1.5" fill="none" />
                </RestoreIcon>
              ) : (
                <MaximizeIcon viewBox="0 0 16 16">
                  <rect x="2" y="2" width="12" height="12" rx="1" />
                </MaximizeIcon>
              )}
            </TimerModalButton>
            <TimerModalButton
              onClick={onClose}
              title="닫기"
            >
              ×
            </TimerModalButton>
          </TimerModalControls>
        </TimerModalHeader>
        
        {isMinimized && (
          <MinimizedProgressBarContainer>
            <MinimizedProgressBar 
              $progress={progress} 
              $isUnderOneMinute={isUnderOneMinute}
            />
          </MinimizedProgressBarContainer>
        )}
        <TimerModalBody $isMinimized={isMinimized}>
          {isMinimized ? (
            <MinimizedTimerDisplay>
              <MinimizedTimerText>{formatTime(remainingSeconds)}</MinimizedTimerText>
              <MinimizedTimerButtonWrapper>
                <MinimizedPlayStopButton
                  $isRunning={isRunning}
                  onClick={isRunning ? handlePause : handleStart}
                  title={isRunning ? "일시정지" : "시작"}
                  aria-label={isRunning ? "일시정지" : "시작"}
                >
                  {isRunning ? (
                    <MinimizedStopIcon viewBox="0 0 24 24">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </MinimizedStopIcon>
                  ) : (
                    <MinimizedPlayIcon viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </MinimizedPlayIcon>
                  )}
                </MinimizedPlayStopButton>
              </MinimizedTimerButtonWrapper>
            </MinimizedTimerDisplay>
          ) : (
            <>
              <TimerDisplaySection $modalWidth={modalWidth} $modalHeight={modalHeight}>
                <TimerCircleContainer $size={circleSize}>
                  <QuickTimeButtonContainer $top={quickButtonTop}>
                    <QuickTimeButton 
                      onClick={() => handleAddTime(300)} 
                      disabled={isRunning} 
                      title="5분 추가"
                      $fontSize={buttonFontSize}
                      $padding={buttonPadding}
                    >
                      5분
                    </QuickTimeButton>
                    <QuickTimeButton 
                      onClick={() => handleAddTime(60)} 
                      disabled={isRunning} 
                      title="1분 추가"
                      $fontSize={buttonFontSize}
                      $padding={buttonPadding}
                    >
                      1분
                    </QuickTimeButton>
                    <QuickTimeButton 
                      onClick={() => handleAddTime(10)} 
                      disabled={isRunning} 
                      title="10초 추가"
                      $fontSize={buttonFontSize}
                      $padding={buttonPadding}
                    >
                      10초
                    </QuickTimeButton>
                    <QuickTimeButton 
                      onClick={handleReset} 
                      disabled={isRunning} 
                      title="리셋"
                      $fontSize={buttonFontSize}
                      $padding={buttonPadding}
                    >
                      리셋
                    </QuickTimeButton>
                  </QuickTimeButtonContainer>
                  <svg
                    width={circleSize}
                    height={circleSize}
                    viewBox={`0 0 ${circleSize} ${circleSize}`}
                    preserveAspectRatio="xMidYMid meet"
                    style={{ transform: 'rotate(-90deg)' }}
                  >
                    {/* 배경 원 */}
                    <circle
                      cx={circleSize / 2}
                      cy={circleSize / 2}
                      r={radius}
                      fill="none"
                      stroke="rgba(255, 255, 255, 0.3)"
                      strokeWidth={circleSize / 17.5}
                    />
                    {isStopwatch ? (
                      <>
                        {/* 스탑워치 모드: 완료된 1분마다 원형 레이어 (짝수=흰색, 홀수=다크모드:#5B5B5B/라이트모드:#5F8581) */}
                        {Array.from({ length: completedMinutes }, (_, i) => {
                          const isEven = i % 2 === 0;
                          return (
                            <circle
                              key={`completed-${i}`}
                              cx={circleSize / 2}
                              cy={circleSize / 2}
                              r={radius}
                              fill="none"
                              stroke={getStopwatchColor(isEven)}
                              strokeWidth={circleSize / 17.5}
                              strokeDasharray={circumference}
                              strokeDashoffset={0}
                              strokeLinecap="round"
                              style={{
                                transform: 'scaleY(-1)',
                                transformOrigin: 'center',
                              }}
                            />
                          );
                        })}
                        {/* 현재 1분 내 진행 원 (짝수=흰색, 홀수=다크모드:#5B5B5B/라이트모드:#5F8581) */}
                        {/* 스탑워치 모드: 12시 방향부터 시계방향으로 생성 */}
                        {/* 1분이 완료되는 순간(remainingSeconds % 60 === 0)에는 표시하지 않음 */}
                        {remainingSeconds % 60 !== 0 && (
                          <circle
                            cx={circleSize / 2}
                            cy={circleSize / 2}
                            r={radius}
                            fill="none"
                            stroke={getStopwatchColor(isCurrentMinuteEven)}
                            strokeWidth={circleSize / 17.5}
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            strokeLinecap="round"
                            style={{
                              transition: 'stroke-dashoffset 0.3s ease',
                            }}
                          />
                        )}
                      </>
                    ) : (
                      /* 타이머 모드: 기존 진행 원 */
                    <circle
                      cx={circleSize / 2}
                      cy={circleSize / 2}
                      r={radius}
                      fill="none"
                      stroke={isUnderOneMinute ? '#ff6b6b' : 'white'}
                      strokeWidth={circleSize / 17.5}
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      strokeLinecap="round"
                      style={{
                        transition: 'stroke-dashoffset 0.3s ease, stroke 0.3s ease',
                        transform: 'scaleY(-1)',
                        transformOrigin: 'center',
                      }}
                    />
                    )}
                  </svg>
                  <TimerTextContainer $size={circleSize}>
                    <TimerTextWrapper>
                    {editingPart === 'minutes' && !isRunning ? (
                      <>
                        <TimerTextInput
                          type="text"
                          value={editingMinutes}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            if (value.length <= 2) {
                              setEditingMinutes(value);
                            }
                          }}
                          onBlur={handleMinutesBlur}
                          onKeyDown={handleMinutesKeyDown}
                          autoFocus
                          placeholder="00"
                          $fontSize={textFontSize}
                          $width={textWidth}
                        />
                        <TimerTextSeparator $fontSize={textFontSize}>:</TimerTextSeparator>
                        <TimerTextPart 
                          onClick={handleSecondsClick}
                          $fontSize={textFontSize}
                          $width={textWidth}
                        >
                          {String(Math.floor(remainingSeconds % 60)).padStart(2, '0')}
                        </TimerTextPart>
                      </>
                    ) : editingPart === 'seconds' && !isRunning ? (
                      <>
                        <TimerTextPart 
                          onClick={handleMinutesClick}
                          $fontSize={textFontSize}
                          $width={textWidth}
                        >
                          {String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}
                        </TimerTextPart>
                        <TimerTextSeparator $fontSize={textFontSize}>:</TimerTextSeparator>
                        <TimerTextInput
                          type="text"
                          value={editingSeconds}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            if (value.length <= 2) {
                              setEditingSeconds(value);
                            }
                          }}
                          onBlur={handleSecondsBlur}
                          onKeyDown={handleSecondsKeyDown}
                          autoFocus
                          placeholder="00"
                          $fontSize={textFontSize}
                          $width={textWidth}
                        />
                      </>
                    ) : (
                      <>
                        <TimerTextPart 
                          onClick={handleMinutesClick}
                          $fontSize={textFontSize}
                          $width={textWidth}
                        >
                          {String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}
                        </TimerTextPart>
                        <TimerTextSeparator $fontSize={textFontSize}>:</TimerTextSeparator>
                        <TimerTextPart 
                          onClick={handleSecondsClick}
                          $fontSize={textFontSize}
                          $width={textWidth}
                        >
                          {String(remainingSeconds % 60).padStart(2, '0')}
                        </TimerTextPart>
                      </>
                    )}
                    </TimerTextWrapper>
                    <TimerButtonWrapper>
                  {!isRunning ? (
                    <TimerButton 
                      $variant="primary" 
                      onClick={handleStart}
                      $padding={buttonPadding}
                      $fontSize={buttonFontSize}
                    >
                      시작
                    </TimerButton>
                  ) : (
                    <TimerButton 
                      $variant="secondary" 
                      onClick={handlePause}
                      $padding={buttonPadding}
                      $fontSize={buttonFontSize}
                    >
                      정지
                    </TimerButton>
                  )}
                    </TimerButtonWrapper>
                  </TimerTextContainer>
                </TimerCircleContainer>

                <VolumeControlContainer>
                  <VolumeIconButton onClick={handleMuteToggle} title={isMuted ? "음소거 해제" : "음소거"}>
                    {isMuted ? (
                      <svg viewBox="0 0 24 24">
                        <path d="M11 5L6 9H2v6h4l5 4V5z" />
                        <line x1="23" y1="9" x2="17" y2="15" />
                        <line x1="17" y1="9" x2="23" y2="15" />
                      </svg>
                    ) : volume === 0 ? (
                      <svg viewBox="0 0 24 24">
                        <path d="M11 5L6 9H2v6h4l5 4V5z" />
                        <line x1="23" y1="9" x2="17" y2="15" />
                        <line x1="17" y1="9" x2="23" y2="15" />
                      </svg>
                    ) : volume < 0.5 ? (
                      <svg viewBox="0 0 24 24">
                        <path d="M11 5L6 9H2v6h4l5 4V5z" />
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24">
                        <path d="M11 5L6 9H2v6h4l5 4V5z" />
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                      </svg>
                    )}
                  </VolumeIconButton>
                  <VolumeSliderContainer 
                    data-volume-slider
                    onClick={handleVolumeSliderClick}
                    onMouseDown={handleVolumeSliderMouseDown}
                  >
                    <VolumeSliderTrack $volume={isMuted ? 0 : volume} />
                    <VolumeSliderThumb $volume={isMuted ? 0 : volume} />
                  </VolumeSliderContainer>
                  <VolumeSpacer />
                </VolumeControlContainer>
              </TimerDisplaySection>
            </>
          )}
        </TimerModalBody>
        
        {!isMinimized && !isMaximized && (
          <>
            {/* 왼쪽 상단 */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '20px',
                height: '20px',
                cursor: 'nwse-resize',
                background: 'transparent',
                zIndex: 10,
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
            />
            {/* 오른쪽 상단 */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '20px',
                height: '20px',
                cursor: 'nesw-resize',
                background: 'transparent',
                zIndex: 10,
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
            />
            {/* 왼쪽 하단 */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '20px',
                height: '20px',
                cursor: 'nesw-resize',
                background: 'transparent',
                zIndex: 10,
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
            />
            {/* 오른쪽 하단 */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '20px',
                height: '20px',
                cursor: 'nwse-resize',
                background: 'transparent',
                zIndex: 10,
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
            />
          </>
        )}
      </TimerModalContent>
    </TimerModalOverlay>,
    document.body
  );
};

export default TimerModal;

